import SyntaxHighlighter from 'react-syntax-highlighter';
import { useFormContext } from 'react-hook-form';
import { MenuItem } from '@material-ui/core';
import { Trans } from '@lingui/macro';
import _ from 'lodash';
import { usePortalLocation } from '../../../stores/EnvironmentStore';
import NgpCopyToClipboard from '../../form/NgpCopyToClipboard';
import NgpTextField from '../../form/NgpTextField';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT } from '../../../utilities/FormUtilities';
import {
    SAML_DEFAULT_ROLE_FIELD,
    SAML_GROUP_ROLE_ASSOCIATIONS_FIELD,
    SAML_GROUP_USER_MEMBERSHIP_ATTR_FIELD,
    SAML_METADATA_FIELD,
    SAML_PROVIDER_FIELD,
    SAML_USER_EMAIL_ADDRESS_ATTR_FIELD,
    SAML_USER_FIRST_NAME_ATTR_FIELD,
    SAML_USER_LAST_NAME_ATTR_FIELD,
    SAML_USER_USERNAME_ATTR_FIELD,
    UNAUTHORIZED_OPTION_TEXT,
    UNAUTHORIZED_OPTION_VALUE,
} from './SsoAdminConstants';
import NgpFileUpload from '../../form/NgpFileUpload';
import NgpSelect from '../../form/NgpSelect';
import ListFormField from '../../form/ListFormField';
import { getGroupAssociationColumns } from './SsoAdminHelper';
import GroupForm from './GroupForm';

function getSSOUrl(portalLocation) {
    const ngpSSOEndPoint = '/ipm/sso/saml/acs';
    return `${_.trimEnd(portalLocation, '/')}${ngpSSOEndPoint}`;
}

const groupAssociationColumns = getGroupAssociationColumns(false);

export default function Saml({ formData, setDirty, userRoleOptions, classes }) {
    const { watch } = useFormContext();
    const [portalLocation] = usePortalLocation();
    const ssoUrl = getSSOUrl(portalLocation);
    const metadata = watch(SAML_METADATA_FIELD);

    return (
        <>
            <FormSection title={<Trans>SSO URL</Trans>}>
                <NgpCopyToClipboard label={<Trans>SSO URL</Trans>} inputProps={{ size: 65 }} value={ssoUrl} />
            </FormSection>

            <FormSection
                title={<Trans>Identity Provider Configuration</Trans>}
                subText={
                    <Trans>This section defines how nGeniusPULSE will connect to your SAML identity provider</Trans>
                }
            >
                <NgpTextField
                    fieldName={SAML_PROVIDER_FIELD}
                    label={<Trans>Provider Name</Trans>}
                    required
                    inputProps={{
                        size: 40,
                        maxLength: 40,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                />

                <div className={classes.samlXMLContainer}>
                    <NgpFileUpload
                        fieldName={SAML_METADATA_FIELD}
                        shrink
                        helpText={<Trans>Please provide the metadata XML file from your SAML provider</Trans>}
                    />
                </div>

                {metadata && <SyntaxHighlighter language="xml-doc">{metadata}</SyntaxHighlighter>}
            </FormSection>

            <FormSection
                title={<Trans>User Configuration</Trans>}
                subText={
                    <Trans>
                        This section defines how to link users between your SAML identity provider and nGeniusPULSE
                    </Trans>
                }
            >
                <NgpTextField
                    fieldName={SAML_USER_FIRST_NAME_ATTR_FIELD}
                    label={<Trans>First Name Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={<Trans>The SAML attribute which contains a user&apos;s first name.</Trans>}
                />

                <NgpTextField
                    fieldName={SAML_USER_LAST_NAME_ATTR_FIELD}
                    label={<Trans>Last Name Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={<Trans>The SAML attribute which contains a user&apos;s last name.</Trans>}
                />

                <NgpTextField
                    fieldName={SAML_USER_USERNAME_ATTR_FIELD}
                    label={<Trans>Username Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={<Trans>The SAML attribute which contains a user&apos;s username.</Trans>}
                />

                <NgpTextField
                    fieldName={SAML_USER_EMAIL_ADDRESS_ATTR_FIELD}
                    label={<Trans>Email Address Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={<Trans>The SAML attribute which contains a user&apos;s email address.</Trans>}
                />

                <NgpSelect
                    fieldName={SAML_DEFAULT_ROLE_FIELD}
                    label={<Trans>Default User Role</Trans>}
                    helpTipText={
                        <Trans>
                            If a user does not match a group in the Group Configuration section, this role will be
                            automatically selected. If &quot;Unauthorized&quot; is selected, that user will not be
                            allowed access into nGeniusPULSE.
                        </Trans>
                    }
                >
                    <MenuItem key={1} value={UNAUTHORIZED_OPTION_VALUE}>
                        {UNAUTHORIZED_OPTION_TEXT}
                    </MenuItem>
                    {_.map(userRoleOptions, (value, key) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </NgpSelect>
            </FormSection>

            <FormSection
                title={<Trans>Group Configuration</Trans>}
                subText={
                    <Trans>
                        This section defines the relationship between the groups that users may belong to in the SAML
                        identity provider and what role they will be given in nGeniusPULSE on first login
                    </Trans>
                }
                addSpacer
            >
                <NgpTextField
                    fieldName={SAML_GROUP_USER_MEMBERSHIP_ATTR_FIELD}
                    label={<Trans>Group Membership Attribute</Trans>}
                    required
                    inputProps={{
                        size: 60,
                        maxLength: 4096,
                        autoComplete: AUTO_COMPLETE_DEFAULT,
                    }}
                    helpTipText={<Trans>The SAML attribute which contains the user&apos;s group membership.</Trans>}
                />

                <div className={classes.samlGroupConfigurationSpacer} />

                <ListFormField
                    fieldName={SAML_GROUP_ROLE_ASSOCIATIONS_FIELD}
                    dbSelected={_.get(formData, SAML_GROUP_ROLE_ASSOCIATIONS_FIELD)}
                    gridColumns={groupAssociationColumns}
                    setSelectionIsDirty={setDirty}
                    formComponent={<GroupForm isActiveDirectory={false} />}
                />
            </FormSection>
        </>
    );
}
